import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PageLayout from "@/components/PageLayout";
import SEO from "@/components/SEO";
import FAQSchema from "@/components/FAQSchema";
import ContactModal from "@/components/new-landing/ContactModal";
import { trackEvent } from "@/utils/analytics";

const NAVY = "#002178";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 },
};

const comparisonRows = [
  {
    label: "Primary focus",
    cpo: "Product strategy, user experience, and product-led growth",
    coo: "Operational efficiency, process optimization, and scale",
  },
  {
    label: "Growth lever",
    cpo: "The product: better activation, retention, and expansion",
    coo: "The operations: faster delivery, lower costs, smoother execution",
  },
  {
    label: "Key metrics",
    cpo: "Activation rate, retention, NRR, product-qualified leads",
    coo: "Operational margin, delivery velocity, process efficiency",
  },
  {
    label: "Best for",
    cpo: "Companies where the product is the primary growth engine",
    coo: "Companies where execution and operational scale are the bottleneck",
  },
  {
    label: "Deliverables",
    cpo: "Product roadmap, growth experiments, team coaching",
    coo: "Process documentation, org design, operational playbooks",
  },
];

const faqs = [
  {
    question: "What is the difference between a CPO and a COO?",
    answer:
      "A CPO focuses on what the product should be and how it drives business growth. A COO focuses on how the organization operates and delivers. The CPO is concerned with product-market fit, user experience, and revenue growth through the product. The COO is concerned with operational efficiency, team structure, and execution at scale.",
  },
  {
    question:
      "When should a company choose FractionalCPO over a fractional COO?",
    answer:
      "When the problem is growth, not operations. If your company runs smoothly but the product isn't converting users, retaining customers, or driving revenue expansion, you need FractionalCPO. A COO won't fix a product-market fit problem, and a CPO won't fix a broken supply chain. Match the role to the bottleneck.",
  },
  {
    question: "Can FractionalCPO and a fractional COO work together?",
    answer:
      "Absolutely. In PE-backed technology companies, this combination is common. FractionalCPO defines what the product should do and how it should grow. The COO ensures the organization can deliver on that vision at scale. We drive what gets built; the COO drives how efficiently it gets delivered.",
  },
];

const FractionalCPOvsCOO = () => {
  const [isContactOpen, setIsContactOpen] = useState(false);

  return (
    <PageLayout>
      <SEO
        title="Fractional CPO vs Fractional COO: Product vs Operations"
        description="Understand the difference between a Fractional CPO and Fractional COO. Learn when your technology company needs product leadership vs operational leadership."
        path="/fractional-cpo-vs-coo"
        schema={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Fractional CPO vs Fractional COO: Product vs Operations",
          "url": "https://fractionalcpo.com/fractional-cpo-vs-coo",
          "dateModified": "2026-06-17",
          "description": "Understand the difference between a Fractional CPO and Fractional COO. Learn when your technology company needs product leadership vs operational leadership.",
          "isPartOf": { "@type": "WebSite", "url": "https://fractionalcpo.com" }
        }}
      />

      {/* Hero */}
      <section className="py-24 md:py-40 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1400px] mx-auto">
          <motion.p
            {...fadeUp}
            className="text-sm uppercase tracking-[0.3em] text-[#002178] mb-8 font-medium"
          >
            Comparison
          </motion.p>

          <motion.h1
            {...fadeUp}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="text-[clamp(2.5rem,8vw,6rem)] font-light leading-[0.95] tracking-[-0.03em] mb-12"
          >
            Fractional CPO vs{" "}
            <span className="text-[#002178] font-medium">Fractional COO</span>
          </motion.h1>

          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="max-w-3xl"
          >
            <p className="text-xl md:text-2xl leading-relaxed font-medium text-[#1a1a1a] mb-6">
              Product-led growth vs. operational efficiency. Different problems, different leaders.
            </p>
            <p className="text-lg text-black/60 leading-relaxed mb-4">
              A CPO drives growth through the product. Better activation,
              stronger retention, higher expansion revenue. The product becomes
              the growth engine.
            </p>
            <p className="text-lg text-black/60 leading-relaxed">
              A COO drives efficiency through operations. Faster delivery, lower
              costs, smoother processes. The organization runs better.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#F5F5F0]">
        <div className="max-w-[1400px] mx-auto">
          <motion.h2
            {...fadeUp}
            className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] mb-16"
          >
            How they{" "}
            <span className="text-[#002178] font-medium">compare</span>
          </motion.h2>

          {/* Desktop Table */}
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="hidden md:block overflow-x-auto"
          >
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b-2 border-black/10">
                  <th className="text-left py-4 pr-6 text-sm uppercase tracking-[0.2em] text-black/40 font-medium w-[20%]" />
                  <th className="text-left py-4 px-4 text-sm uppercase tracking-[0.2em] text-[#002178] font-medium w-[40%]">
                    Fractional CPO
                  </th>
                  <th className="text-left py-4 px-4 text-sm uppercase tracking-[0.2em] text-black/40 font-medium w-[40%]">
                    Fractional COO
                  </th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row, i) => (
                  <tr
                    key={i}
                    className="border-b border-black/5 hover:bg-black/[0.02] transition-colors"
                  >
                    <td className="py-5 pr-6 text-sm font-medium text-black/60">
                      {row.label}
                    </td>
                    <td className="py-5 px-4 text-sm font-medium text-[#1a1a1a]">
                      {row.cpo}
                    </td>
                    <td className="py-5 px-4 text-sm text-black/50">
                      {row.coo}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-6">
            {comparisonRows.map((row, i) => (
              <motion.div
                key={i}
                {...fadeUp}
                transition={{ delay: i * 0.08 }}
                className="border-b border-black/10 pb-6"
              >
                <p className="text-sm font-medium text-black/40 uppercase tracking-[0.15em] mb-3">
                  {row.label}
                </p>
                <div className="space-y-3">
                  <div className="bg-[#002178]/[0.04] p-3 rounded">
                    <p className="text-xs text-[#002178] font-medium mb-1">
                      Fractional CPO
                    </p>
                    <p className="text-sm font-medium">{row.cpo}</p>
                  </div>
                  <div className="p-3">
                    <p className="text-xs text-black/40 font-medium mb-1">
                      Fractional COO
                    </p>
                    <p className="text-sm text-black/60">{row.coo}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* When You Need a CPO, Not a COO */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1400px] mx-auto">
          <div className="max-w-3xl">
            <motion.h2
              {...fadeUp}
              className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] mb-8"
            >
              When you need a CPO,{" "}
              <span className="text-[#002178] font-medium">not a COO</span>
            </motion.h2>
            <motion.div
              {...fadeUp}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="space-y-4"
            >
              <p className="text-lg text-black/60 leading-relaxed">
                When the problem is product-market fit, conversion, or growth,
                not operational execution. When the team ships on time but the
                product still isn't moving the revenue needle.
              </p>
              <p className="text-lg text-black/60 leading-relaxed">
                A COO can optimize delivery speed and reduce costs. But if you're
                efficiently building the wrong things, operational excellence won't
                save you. Product leadership connects what you build to why it
                matters for the business.
              </p>
              <p className="text-lg text-black/60 leading-relaxed">
                PE-backed companies often discover this during post-acquisition
                reviews. The operations may run fine, but the product hasn't
                evolved to drive the growth the investment thesis requires.
                That's a{" "}
                <Link
                  to="/what-is-a-fractional-cpo"
                  className="text-[#002178] underline underline-offset-4 hover:text-[#002178]/80"
                >
                  FractionalCPO
                </Link>{" "}
                problem.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        className="py-24 md:py-32 px-6 md:px-12 lg:px-24"
        style={{ backgroundColor: NAVY }}
      >
        <div className="max-w-[1400px] mx-auto">
          <div className="max-w-3xl">
            <motion.h2
              {...fadeUp}
              className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] text-white mb-8"
            >
              Is your growth gap a{" "}
              <span className="font-medium">product problem?</span>
            </motion.h2>

            <motion.p
              {...fadeUp}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-lg text-white/70 leading-relaxed mb-8"
            >
              Our{" "}
              <Link
                to="/assessment"
                className="text-white underline underline-offset-4 hover:text-white/90"
              >
                product assessment
              </Link>{" "}
              helps you identify whether the bottleneck is product strategy,
              operational execution, or both. Let's figure it out together.
            </motion.p>

            <motion.div {...fadeUp} transition={{ duration: 0.6, delay: 0.2 }}>
              <button
                onClick={() => { trackEvent('contact_click', { source: 'cta_block' }); setIsContactOpen(true); }}
                className="inline-block px-8 py-4 bg-white text-[#002178] font-medium hover:scale-105 transition-transform"
              >
                Let's chat
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <div className="bg-[#F5F5F0]">
        <FAQSchema faqs={faqs} heading="Frequently Asked Questions" />
      </div>

      <ContactModal open={isContactOpen} onOpenChange={setIsContactOpen} />
    </PageLayout>
  );
};

export default FractionalCPOvsCOO;
